import { ParameterSeries } from '../types';
import { assetUrl } from './asset';

export type GridSeries = {
  time: string[]; // ISO YYYY-MM-DD
  sm: number[];   // daily mean soil moisture (m3/m3)
  rain: number[]; // daily total rainfall (mm)
};

// Try multiple local paths where tiles may be published
async function fetchTileAny(latDeg: number, lonDeg: number): Promise<any | null> {
  const candidates = [
    assetUrl(`/era5_sm/lat${latDeg}_lon${lonDeg}.json`),
    assetUrl(`/india_data/era5_india_grid/2024_08_10/lat${latDeg}_lon${lonDeg}.json`),
  ];
  for (const url of candidates) {
    try {
      const res = await fetch(url);
      if (res.ok) return await res.json();
    } catch {}
  }
  return null;
}

// Given lat/lon, load the corresponding 1° tile file and return nearest-point series
export async function fetchNearestGridSeries(lat: number, lon: number): Promise<GridSeries | null> {
  const latDeg = Math.floor(lat);
  const lonDeg = Math.floor(lon);
  const tile = await fetchTileAny(latDeg, lonDeg);
  if (!tile) return null;
  const pts: Array<{ lat: number; lon: number; sm: number[]; rain: number[] }> = tile.points || [];
  if (!pts.length) return null;
  let best = pts[0];
  let bestD = Number.POSITIVE_INFINITY;
  for (const p of pts) {
    const d = haversineSq(lat, lon, p.lat, p.lon);
    if (d < bestD) { bestD = d; best = p; }
  }
  return { time: tile.time, sm: best.sm, rain: best.rain };
}

function haversineSq(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (x: number) => (x * Math.PI) / 180;
  const dLat = toRad(bLat - aLat);
  const dLon = toRad(bLon - aLon);
  const s1 = Math.sin(dLat / 2);
  const s2 = Math.sin(dLon / 2);
  return s1 * s1 + Math.cos(toRad(aLat)) * Math.cos(toRad(bLat)) * s2 * s2;
}

export function toParameterSeries(name: 'SM' | 'PRECTOT', time: string[], values: Array<number | null | undefined>): ParameterSeries {
  const series = time
    .map((iso, i) => {
      const v = values[i];
      if (v == null || !isFinite(Number(v))) return null;
      const dt = new Date(iso + 'T00:00:00Z');
      const year = dt.getUTCFullYear();
      const start = Date.UTC(year, 0, 0);
      const doy = Math.floor((dt.getTime() - start) / (24 * 3600 * 1000));
      return { year, doy, value: Number(v) };
    })
    .filter(Boolean) as any;
  const latest = series[series.length - 1];
  if (name === 'SM') {
    return { display: 'Soil Moisture (ERA5-Land, Surface Layer)', unit: 'm3/m3', series, latest };
  }
  return { display: 'Rainfall (Daily Total)', unit: 'mm/day', series, latest };
}
